import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { QrCode, Play, Star, Clock, Shield, Zap, Users, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const QRPopup = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.div
      className={`hidden lg:flex flex-col items-center absolute z-30 top-32 right-0 transition-all duration-300 ${expanded ? 'w-64 h-80 p-6' : 'w-14 h-40 p-0.5'} bg-gradient-to-br from-red-600 to-orange-500 rounded-l-2xl shadow-2xl cursor-pointer group`}
      style={{borderTopLeftRadius: '2rem', borderBottomLeftRadius: '2rem'}}
      onClick={() => setExpanded(e => !e)}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Notch Tab */}
      {!expanded && (
        <div className="flex flex-col items-center justify-center h-full w-full select-none">
          <div className="flex-1 flex items-center justify-center">
            <QrCode className="w-6 h-6 text-white mb-2" />
          </div>
          <div className="flex-1 flex items-center justify-center">
            <span className="text-white font-semibold text-sm" style={{writingMode: 'vertical-rl', transform: 'rotate(180deg)'}}>QR</span>
          </div>
        </div>
      )}
      {expanded && (
        <>
          <div className="bg-white rounded-xl flex items-center justify-center w-40 h-40 mb-4">
            <div className="w-full h-full bg-black rounded-lg relative">
              <div className="absolute inset-1 bg-white rounded-md">
                <div className="grid grid-cols-8 gap-0.5 p-1">
                  {Array.from({length: 64}).map((_, i) => (
                    <div key={i} className={`aspect-square ${Math.random() > 0.5 ? 'bg-black' : 'bg-white'} rounded-sm`}></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-white font-medium select-none transition-all">Scan to Download</p>
          <div className="text-white text-sm mt-2 text-center">Get the Dailzy app for quick ordering and tracking!</div>
        </>
      )}
    </motion.div>
  );
};

const StatsCounter = ({ end, duration = 2000, label }: { end: number; duration?: number; label: string }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTimestamp: number;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [end, duration]);

  return (
    <div className="text-center">
      <div className="text-2xl sm:text-3xl font-bold text-gray-900">{count.toLocaleString()}+</div>
      <div className="text-sm text-gray-600">{label}</div>
    </div>
  );
};

const Hero = () => {
  const [currentDeliveryTime, setCurrentDeliveryTime] = useState("12");

  useEffect(() => {
    const interval = setInterval(() => {
      const times = ["8", "12", "15", "18", "10"];
      setCurrentDeliveryTime(times[Math.floor(Math.random() * times.length)]);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-orange-50 pt-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* <QRPopup /> */}
      
      <div className="container mx-auto px-4 py-12 sm:py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div 
            className="text-center lg:text-left"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Tagline Badge */}
            <motion.div 
              className="mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-3 rounded-full text-lg font-semibold tracking-wide shadow-lg">
                <Zap className="w-5 h-5" />
                Too busy? Do dailzy
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Get Everything in{" "}
              <span className="relative">
                <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                  {currentDeliveryTime} Minutes
                </span>
                <motion.div
                  className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                />
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              From daily groceries to luxury goods - delivered instantly from trusted local vendors. 
              <span className="font-semibold text-gray-800"> No hidden charges, just pure convenience.</span>
            </motion.p>

            {/* Trust Indicators */}
            <motion.div 
              className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Star className="w-5 h-5 text-yellow-500 fill-current" />
                <span className="font-semibold">4.8/5</span>
                <span className="text-gray-500">(50K+ reviews)</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Shield className="w-5 h-5 text-green-500" />
                <span>100% Secure</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle className="w-5 h-5 text-blue-500" />
                <span>Quality Assured</span>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-lg px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
              >
                <Play className="w-5 h-5 mr-2" />
                Order Now - It's Free!
              </Button>
              <Link to="/vendors" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white text-lg px-8 py-4 transition-all duration-300 w-full"
                >
                  <Users className="w-5 h-5 mr-2" />
                  Join as Vendor
                </Button>
              </Link>
            </motion.div>

            {/* Stats Section */}
            <motion.div 
              className="grid grid-cols-3 gap-8 py-8 border-t border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              <StatsCounter end={1000} label="Local Vendors" />
              <StatsCounter end={50} label="Cities Covered" />
              <StatsCounter end={100} label="Daily Orders (K)" />
            </motion.div>

            {/* Features List */}
            <motion.div 
              className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-gray-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span>10-20 Min Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span>No Hidden Charges</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Fresh & Quality</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>24/7 Available</span>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Phone Mockup Section */}
          <motion.div 
            className="relative flex justify-center lg:justify-end"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <div className="relative perspective-1000">
              {/* Floating Elements */}
              <motion.div
                className="absolute -top-8 -left-8 bg-white rounded-2xl p-4 shadow-lg z-20"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Clock className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-900">Super Fast</div>
                    <div className="text-xs text-gray-500">10-20 min delivery</div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="absolute -bottom-8 -right-8 bg-white rounded-2xl p-4 shadow-lg z-20"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Shield className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-900">100% Safe</div>
                    <div className="text-xs text-gray-500">Quality assured</div>
                  </div>
                </div>
              </motion.div>

              {/* Main Phone Mockup */}
              <motion.div 
                className="relative transform-gpu"
                animate={{ 
                  y: [0, -10, 0],
                  rotateY: [0, 5, 0, -5, 0]
                }}
                transition={{ 
                  duration: 6, 
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="w-72 h-[580px] bg-gray-900 rounded-[3rem] p-2 shadow-2xl relative">
                  {/* Phone Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent rounded-[3rem] transform -skew-x-12 animate-shine"></div>
                  
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
                    {/* Phone Screen Content */}
                    <div className="h-full bg-gray-50 relative">
                      {/* Status Bar */}
                      <div className="flex justify-between items-center px-6 pt-4 pb-2 bg-white">
                        <span className="text-lg font-semibold">3:43</span>
                        <div className="flex items-center gap-1">
                          <div className="flex gap-1">
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                          </div>
                          <div className="text-sm">📶</div>
                          <div className="w-6 h-3 bg-black rounded-sm"></div>
                        </div>
                      </div>

                      {/* Header */}
                      <div className="px-6 py-4 bg-white">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <img src="/lovable-uploads/8936458b-f120-42aa-a567-eac1e09a8296.png" alt="Dailzy" className="w-8 h-8 mr-2" />
                            <h1 className="text-xl font-semibold">Dailzy</h1>
                          </div>
                          <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">🛒</span>
                          </div>
                        </div>
                        
                        {/* Search Bar */}
                        <div className="bg-gray-100 rounded-xl p-3 shadow-inner">
                          <div className="text-gray-400 text-sm">Search for groceries, vegetables...</div>
                        </div>
                      </div>

                      {/* Live Order Tracking */}
                      <div className="px-6 my-4">
                        <motion.div 
                          className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 text-white shadow-lg"
                          animate={{ scale: [1, 1.02, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <div className="text-sm opacity-90">Your Order #1234</div>
                          <div className="text-lg font-semibold">Being Prepared</div>
                          <div className="flex items-center mt-2">
                            <motion.div 
                              className="w-3 h-3 bg-white rounded-full mr-2"
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ duration: 1, repeat: Infinity }}
                            />
                            <div className="text-sm opacity-90">Estimated delivery: {currentDeliveryTime} min</div>
                          </div>
                        </motion.div>
                      </div>
                      
                      {/* Categories */}
                      <div className="px-6">
                        <h2 className="text-lg font-semibold mb-3">Categories</h2>
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { emoji: "🥬", label: "Vegetables" },
                            { emoji: "🍎", label: "Fruits" },
                            { emoji: "🥛", label: "Dairy" },
                            { emoji: "🍞", label: "Groceries" },
                            { emoji: "💊", label: "Medicine" },
                            { emoji: "💄", label: "Cosmetics" }
                          ].map((category, index) => (
                            <motion.div 
                              key={index}
                              className="bg-white rounded-xl p-3 text-center shadow-sm hover:shadow-md transition-shadow"
                              whileHover={{ scale: 1.05 }}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 1 + index * 0.1 }}
                            >
                              <div className="text-2xl mb-1">{category.emoji}</div>
                              <div className="text-xs font-medium">{category.label}</div>
                            </motion.div>
                          ))}
                        </div>
                      </div>

                      {/* Bottom Navigation */}
                      <div className="absolute bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-t border-gray-100 px-6 py-3">
                        <div className="flex justify-around">
                          <div className="text-center">
                            <div className="text-red-500 text-lg">🏠</div>
                            <div className="text-xs text-red-500 font-semibold">Home</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-400 text-lg">🔍</div>
                            <div className="text-xs text-gray-400">Search</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-400 text-lg">🛒</div>
                            <div className="text-xs text-gray-400">Cart</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-400 text-lg">👤</div>
                            <div className="text-xs text-gray-400">Profile</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
